// Aggregates the live Convex queries the dashboard needs and shapes them
// into the same fields the prototype's MOCK_* fixtures exposed. Views
// that previously read MOCK_USER / MOCK_CAMPAIGNS / MOCK_SOLO_CHARS now
// receive an equivalent `data` object via props.
//
// Phase A only wires what the backend already returns. Fields the
// backend does not yet expose (schedule, blurb, currentQuest, presence,
// announcements, mp/maxMp from char blobs, denormalized DM names, etc.)
// are stubbed with conservative placeholders. Phase B / C / D will
// replace those stubs with real queries.

import React from 'react';
import {
  paletteIndexFor,
  hueShiftFor,
  runeFor,
  iconFor,
} from '../derivations/campaignPresentation';
import { timeAgo } from '../derivations/timeAgo';
import { getEffectiveCampaignFeatures } from '../../../campaignFeatureAccess';
import { getCampaignCharacterKind as getCanonicalCampaignCharacterKind } from '../../../campaignCharacterTargets';

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

const sanitizeCampaignFeatures = (features) => {
  if (!features || typeof features !== 'object') return null;
  const out = {};
  for (const [key, value] of Object.entries(features)) {
    if (key === 'auditLog' || key === 'auditlog') continue;
    out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : null;
};

const initialFromName = (name) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '?';
  return trimmed[0].toUpperCase();
};

const handleFromName = (name) => {
  const trimmed = String(name || '').trim().toLowerCase().replace(/\s+/g, '');
  return trimmed ? `@${trimmed}` : '@you';
};

// Map a Convex character summary (from listCampaignRoster / listMyStandaloneCharacters)
// into the prototype's solo-char shape.
const adaptSoloChar = (row) => ({
  id: row.charId,
  name: row.name,
  cls: `L${row.level || 1} ${row.charClass || 'Adventurer'}`,
  hp: row.currentHp ?? row.maxHp ?? 0,
  maxHp: row.maxHp ?? row.currentHp ?? 1,
  lastPlayed: '',
  avatar: '',
  paletteIndex: 0,
  isNew: false,
});

// Map a Convex announcement row into the prototype's table-note shape.
// `author` is left blank for now (we'd need user-name denormalization to
// fill it). `timeAgo` is computed here so the views don't each re-derive.
const adaptAnnouncement = (row) => ({
  id: row.announcementId,
  pinned: !!row.pinned,
  title: row.title || '',
  body: row.body || '',
  author: '',
  campaignId: row.campaignId,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  authorUserId: row.authorUserId,
  timeAgo: timeAgo(row.createdAt),
});

const adaptWorld = (row) => ({
  id: row.worldId,
  worldId: row.worldId,
  name: row.name,
  tagline: row.description || '',
  description: row.description || '',
  baseThemeId: row.baseThemeId || null,
  baseVariant: row.baseVariant || null,
  myRole: row.myRole || null,
  loreCount: row.loreCount || 0,
  atlasPlaceCount: row.atlasPlaceCount || 0,
  loreCountTruncated: !!row.loreCountTruncated,
  atlasPlaceCountTruncated: !!row.atlasPlaceCountTruncated,
});

const getCampaignCharacterKind = (row) => getCanonicalCampaignCharacterKind(row);

const isDashboardTestCharacter = (row) => getCampaignCharacterKind(row) === 'test';

// Map a Convex character summary into the prototype's campaign-char shape.
const adaptCampaignChar = (row) => {
  const campaignCharacterKind = getCampaignCharacterKind(row);
  const isTestCharacter = campaignCharacterKind === 'test';
  return {
    id: row.charId,
    charId: row.charId,
    name: row.name,
    race: row.race || 'Unknown',
    cls: row.charClass || 'Adventurer',
    level: row.level || 1,
    hp: row.currentHp ?? row.maxHp ?? 0,
    maxHp: row.maxHp ?? row.currentHp ?? 1,
    mp: 0,
    maxMp: 0,
    avatar: '',
    note: '',
    ownerUserId: row.ownerUserId || null,
    isMine: !!row.isMine,
    isPlayable: !isTestCharacter && row.isPlayable !== false,
    campaignCharacterKind,
    isTestCharacter,
    testPresetKey: isTestCharacter ? row.testPresetKey || null : null,
    testPresetCategories: isTestCharacter ? row.testPresetCategories || null : null,
    isPromotedTestCharacter: isTestCharacter && row.isPromotedTestCharacter === true,
  };
};

const buildEmptyData = () => ({
  loading: true,
  user: { name: '', handle: '@you', initial: '?' },
  activeCharacter: null,
  campaigns: EMPTY_ARRAY,
  soloCharacters: EMPTY_ARRAY,
  tableNotes: EMPTY_ARRAY,
  announcementsForCampaign: EMPTY_OBJECT,
  campaignCharacters: EMPTY_OBJECT,
  campaignSandboxCharacters: EMPTY_OBJECT,
  campaignFriends: EMPTY_OBJECT,
  campaignExtra: EMPTY_OBJECT,
  campaignLoreByCampaign: EMPTY_OBJECT,
  loreMigrationPreviewByCampaign: EMPTY_OBJECT,
  campaignLoreLoading: false,
  loreMigrationPreviewLoading: false,
  worlds: EMPTY_ARRAY,
  worldsLoading: false,
  activeCampaignId: null,
});

// Hook signature mirrors how CampaignLanding receives its Convex bindings:
// the parent passes `useQuery` / `useMutation` / `api` so the dashboard
// can be exercised in tests or storybooks with mock implementations.
//
// `selectedCampaignId` is the campaign the user is currently inspecting
// (campaign detail view). When null, roster + invites are skipped.
export const useDashboardData = ({
  useQuery,
  api,
  selectedCampaignId = null,
  includeWorldSummaries = false,
  globalFeatureFlags = null,
}) => {
  const whoAmI = useQuery(api.authHelpers.whoAmI, {});
  const myPreferences = useQuery(api.userPreferences.myPreferences, {});
  const myCampaigns = useQuery(api.campaigns.listMine, {});
  const myWorlds = useQuery(
    api.worldLore.listMyWorldsWithLoreCounts,
    includeWorldSummaries ? {} : 'skip',
  );
  const standalone = useQuery(api.campaigns.listMyStandaloneCharacters, {});
  const selectedCampaignRow = selectedCampaignId
    ? (myCampaigns?.campaigns || []).find((campaign) => campaign.campaignId === selectedCampaignId)
    : null;
  const selectedCampaignIsKnown = !!(
    selectedCampaignId
    && (myCampaigns === undefined || selectedCampaignRow)
  );
  const roster = useQuery(
    api.campaigns.listCampaignRoster,
    selectedCampaignId && selectedCampaignIsKnown ? { campaignId: selectedCampaignId } : 'skip',
  );
  // Cross-campaign recent announcements for the Home view (limit 10).
  const myAnnouncements = useQuery(api.announcements.listMine, { limit: 10 });
  // Per-campaign full announcement list for the campaign detail view.
  const campaignAnnouncements = useQuery(
    api.announcements.listForCampaign,
    selectedCampaignId && selectedCampaignIsKnown ? { campaignId: selectedCampaignId } : 'skip',
  );
  const selectedCampaignHasWorld = !!selectedCampaignRow?.worldEntityId;
  const shouldQuerySelectedCampaignLore = !!(selectedCampaignId && selectedCampaignHasWorld);
  const revealedLore = useQuery(
    api.worldLore.listRevealedCampaignLore,
    shouldQuerySelectedCampaignLore ? { campaignId: selectedCampaignId, limit: 50 } : 'skip',
  );
  const selectedCampaignRole = selectedCampaignRow?.role || null;
  const canManageSelectedCampaignLore = selectedCampaignRole === 'owner' || selectedCampaignRole === 'dm';
  const loreMigrationPreview = useQuery(
    api.worldLore.previewLoreMigrationCandidates,
    shouldQuerySelectedCampaignLore && canManageSelectedCampaignLore
      ? { campaignId: selectedCampaignId, limit: 25 }
      : 'skip',
  );
  const rosterRowsForDashboard = React.useMemo(() => {
    if (!roster || !Array.isArray(roster.characters)) return null;
    return roster.characters.filter((row) => !isDashboardTestCharacter(row));
  }, [roster]);

  // Presence for the roster's owners. The userIds list is memoized so the
  // useQuery args object stays stable while the roster is unchanged.
  // Phase D scope is per-user, so we only need the set of unique owners.
  const presenceUserIds = React.useMemo(() => {
    if (!rosterRowsForDashboard) return null;
    const seen = new Set();
    const out = [];
    for (const row of rosterRowsForDashboard) {
      if (row.ownerUserId && !seen.has(row.ownerUserId)) {
        seen.add(row.ownerUserId);
        out.push(row.ownerUserId);
      }
    }
    return out;
  }, [rosterRowsForDashboard]);
  const presence = useQuery(
    api.presence.listPresenceFor,
    presenceUserIds && presenceUserIds.length > 0 ? { userIds: presenceUserIds } : 'skip',
  );

  return React.useMemo(() => {
    const loading = whoAmI === undefined || myCampaigns === undefined || standalone === undefined;
    if (loading) return { ...buildEmptyData(), loading: true };

    // Display name priority: explicit nickname → first token of provider's
    // legal name → email local-part. We don't want to leak full legal
    // names by default, so even when no nickname is set the dashboard
    // shows "Caroline" rather than "Caroline Kuzio".
    const fullName = (whoAmI?.name || '').trim();
    const firstName = fullName ? (fullName.split(/\s+/)[0] || fullName) : '';
    const nickname = (myPreferences?.nickname || '').trim();
    const displayName = nickname || firstName || whoAmI?.email || 'You';
    const user = {
      name: displayName,
      firstName,
      fullName,
      nickname,
      email: whoAmI?.email || '',
      handle: handleFromName(nickname || firstName || whoAmI?.email),
      initial: initialFromName(displayName),
    };

    const myCampaignsList = myCampaigns?.campaigns || [];
    const activeCampaignId = myCampaigns?.activeCampaignId || null;

    // Map Convex campaign rows into the prototype's campaign shape.
    // Phase B wired schedule/blurb/dmDisplayName/memberCount/sessionCount/
    // currentQuest; Forge (Phase Forge, 2026-05-17) added tagline +
    // description + baseThemeId + enabledFeatures. The card historically
    // showed `blurb` (italic short pitch); we now prefer the Forge's
    // `tagline` field for that slot, falling back to legacy blurb. The
    // longer `description` is exposed separately so the campaign detail
    // hero strips can render it underneath the tagline.
    const campaigns = myCampaignsList.map((c) => {
      const myCharacter = c.myCharacter ? adaptCampaignChar(c.myCharacter) : null;
      const visibleMyCharacter = myCharacter?.isTestCharacter ? null : myCharacter;
      const enabledFeatures = sanitizeCampaignFeatures(c.enabledFeatures || null);
      return {
        id: c.campaignId,
        campaignId: c.campaignId,
        name: c.name,
        role: c.role,
        isActive: c.isActive,
        dm: c.dmDisplayName || (c.role === 'owner' || c.role === 'dm' ? user.name : ''),
        players: c.memberCount || 0,
        schedule: c.schedule || '',
        // `blurb` is the prototype's name for the short pitch on the card;
        // keep that name so we don't have to touch CampaignCard, but read
        // from Forge's `tagline` first.
        blurb: c.tagline || c.blurb || '',
        tagline: c.tagline || c.blurb || '',
        description: c.description || '',
        baseThemeId: c.baseThemeId || null,
        baseVariant: c.baseVariant || null,
        worldMode: c.worldMode || null,
        worldId: c.worldEntityId || c.worldId || null,
        legacyWorldId: c.worldId || null,
        worldEntityId: c.worldEntityId || null,
        newWorldName: c.newWorldName || null,
        enabledFeatures,
        effectiveFeatures: getEffectiveCampaignFeatures(enabledFeatures, globalFeatureFlags),
        homebrew: c.homebrew || null,
        linkedElements: c.linkedElements || null,
        originSourceCampaignId: c.originSourceCampaignId || null,
        rulesSourceCampaignId: c.rulesSourceCampaignId || null,
        sourceCampaignId: c.sourceCampaignId || null,
        templateOrigin: c.templateOrigin || null,
        rulesMode: c.rulesMode || null,
        myCharacter: visibleMyCharacter,
        party: visibleMyCharacter?.name ? [visibleMyCharacter.name] : EMPTY_ARRAY,
        yourChar: visibleMyCharacter?.name || null,
        yourCharId: visibleMyCharacter?.charId || null,
        myCharacterId: visibleMyCharacter?.charId || null,
        icon: iconFor(c.campaignId),
        rune: runeFor(c.campaignId),
        paletteIndex: paletteIndexFor(c.campaignId),
        hueShift: hueShiftFor(c.campaignId),
      };
    });

    const soloCharacters = (standalone || []).map(adaptSoloChar);

    // Campaign-scoped detail comes only from the active roster query.
    // Presence joins on ownerUserId: any roster row whose owner has a
    // recent heartbeat reads as 'online'; everyone else stays 'offline'.
    const presenceByUserId = new Map();
    if (presence && Array.isArray(presence.entries)) {
      for (const entry of presence.entries) {
        presenceByUserId.set(entry.userId, !!entry.isOnline);
      }
    }
    const campaignCharacters = {};
    const campaignSandboxCharacters = {};
    const campaignFriends = {};
    if (roster && roster.campaignId) {
      const selectedCharId = myCampaignsList
        .find((campaign) => campaign.campaignId === roster.campaignId)
        ?.myCharacter?.charId || null;
      const allRosterRows = Array.isArray(roster.characters) ? roster.characters : [];
      campaignSandboxCharacters[roster.campaignId] = allRosterRows
        .map((row) => ({
          ...adaptCampaignChar(row),
          isSelected: !!selectedCharId && row.charId === selectedCharId,
        }));
      const visibleRosterRows = rosterRowsForDashboard || [];
      campaignCharacters[roster.campaignId] = visibleRosterRows
        .map((row) => ({
          ...adaptCampaignChar(row),
          isSelected: !!selectedCharId && row.charId === selectedCharId,
        }));
      campaignFriends[roster.campaignId] = visibleRosterRows.map((row) => ({
        id: row.charId,
        name: row.name,
        role: row.isMine ? 'You' : 'Player',
        status: presenceByUserId.get(row.ownerUserId) ? 'online' : 'offline',
        campaignCharacterKind: getCampaignCharacterKind(row),
        isTestCharacter: isDashboardTestCharacter(row),
        testPresetKey: isDashboardTestCharacter(row) ? row.testPresetKey || null : null,
      }));
    }

    // Per-campaign extras — sessionCount + currentQuest come from listMine
    // (Phase B). `day` mirrors sessionCount until session-end events drive
    // an independent counter (Phase E). `invite` is still surfaced from
    // listCampaignInvites separately and left empty here.
    const campaignExtra = {};
    for (const c of myCampaignsList) {
      const sessionCount = typeof c.sessionCount === 'number' ? c.sessionCount : 0;
      campaignExtra[c.campaignId] = {
        day: sessionCount > 0 ? sessionCount : 1,
        sessionCount,
        invite: '',
        quest: c.currentQuest || '',
      };
    }

    // Active character resolution:
    //   1. If user has an active campaign + loaded roster, use the playable
    //      character they're set up with in that campaign (current behavior).
    //   2. Otherwise, if user has exactly one playable character across
    //      standalone + campaign-owned characters, surface that one on the
    //      banner — common case for new users with a single char who haven't
    //      explicitly "activated" anything yet.
    //   3. Otherwise null (banner hidden).
    const buildCharacterFromRosterRow = (row, campaignId) => {
      const campaignCharacterKind = getCampaignCharacterKind(row);
      const isTestCharacter = campaignCharacterKind === 'test';
      return {
        name: row.name,
        race: row.race || 'Unknown',
        charClass: row.charClass || 'Adventurer',
        level: row.level || 1,
        charId: row.charId,
        id: row.charId,
        campaignId: campaignId || null,
        avatar: '',
        hp: row.currentHp ?? row.maxHp ?? 0,
        maxHp: row.maxHp ?? 1,
        mp: 0,
        maxMp: 0,
        campaignCharacterKind,
        isTestCharacter,
        isPlayable: !isTestCharacter && row.isPlayable !== false,
        testPresetKey: isTestCharacter ? row.testPresetKey || null : null,
        testPresetCategories: isTestCharacter ? row.testPresetCategories || null : null,
        isPromotedTestCharacter: isTestCharacter && row.isPromotedTestCharacter === true,
      };
    };
    const buildCharacterFromStandalone = (row) => ({
      name: row.name,
      race: row.race || 'Unknown',
      charClass: row.charClass || 'Adventurer',
      level: row.level || 1,
      charId: row.charId,
      id: row.charId,
      campaignId: null,
      avatar: '',
      hp: row.currentHp ?? row.maxHp ?? 0,
      maxHp: row.maxHp ?? 1,
      mp: 0,
      maxMp: 0,
    });

    let activeCharacter = null;
    if (activeCampaignId) {
      const activeCampaignWithMyChar = myCampaignsList.find((c) => (
        c.campaignId === activeCampaignId
        && c.myCharacter
        && !isDashboardTestCharacter(c.myCharacter)
        && c.myCharacter.isPlayable !== false
      ));
      if (activeCampaignWithMyChar) {
        activeCharacter = buildCharacterFromRosterRow(
          activeCampaignWithMyChar.myCharacter,
          activeCampaignId,
        );
      }
    }

    if (!activeCharacter && activeCampaignId && roster && roster.campaignId === activeCampaignId) {
      const mine = (rosterRowsForDashboard || []).find((r) => (
        r.isMine && r.isPlayable && !isDashboardTestCharacter(r)
      ));
      if (mine) activeCharacter = buildCharacterFromRosterRow(mine, activeCampaignId);
    }

    if (!activeCharacter) {
      // Count all playable characters the user owns. `standalone` already
      // gives us their standalone chars; `listMine` denormalizes each
      // campaign's `myCharacter` so we don't need per-campaign roster
      // reads to enumerate.
      const standaloneList = standalone || [];
      const campaignsWithMyChar = myCampaignsList
        .filter((c) => (
          c.myCharacter
          && !isDashboardTestCharacter(c.myCharacter)
          && c.myCharacter.isPlayable !== false
        ));
      const totalCharacters = standaloneList.length + campaignsWithMyChar.length;
      if (totalCharacters === 1) {
        if (standaloneList.length === 1) {
          activeCharacter = buildCharacterFromStandalone(standaloneList[0]);
        } else if (campaignsWithMyChar.length === 1) {
          const c = campaignsWithMyChar[0];
          activeCharacter = buildCharacterFromRosterRow(c.myCharacter, c.campaignId);
        }
      }
    }

    // Phase C: announcements. `tableNotes` carries the cross-campaign
    // recent set for the Home view; `announcementsForCampaign[id]` carries
    // the full list for the campaign detail view when one is selected.
    const tableNotes = (myAnnouncements?.announcements || []).map(adaptAnnouncement);
    const worlds = (myWorlds?.worlds || []).map(adaptWorld);
    const announcementsForCampaign = {};
    if (campaignAnnouncements && campaignAnnouncements.campaignId) {
      announcementsForCampaign[campaignAnnouncements.campaignId] =
        (campaignAnnouncements.announcements || []).map(adaptAnnouncement);
    }
    const campaignLoreByCampaign = {};
    if (revealedLore && revealedLore.campaignId) {
      campaignLoreByCampaign[revealedLore.campaignId] = {
        worldId: revealedLore.worldId || null,
        noWorld: !!revealedLore.noWorld,
        entities: revealedLore.entities || EMPTY_ARRAY,
      };
    } else if (selectedCampaignId && selectedCampaignIsKnown && !selectedCampaignHasWorld) {
      campaignLoreByCampaign[selectedCampaignId] = {
        worldId: null,
        noWorld: true,
        entities: EMPTY_ARRAY,
      };
    }
    const loreMigrationPreviewByCampaign = {};
    if (loreMigrationPreview && loreMigrationPreview.campaignId) {
      loreMigrationPreviewByCampaign[loreMigrationPreview.campaignId] = loreMigrationPreview;
    }

    return {
      loading: false,
      user,
      myPreferences: myPreferences || null,
      activeCharacter,
      campaigns,
      soloCharacters,
      tableNotes,
      announcementsForCampaign,
      campaignCharacters,
      campaignSandboxCharacters,
      campaignFriends,
      campaignExtra,
      campaignLoreByCampaign,
      loreMigrationPreviewByCampaign,
      campaignLoreLoading: shouldQuerySelectedCampaignLore ? revealedLore === undefined : false,
      loreMigrationPreviewLoading: shouldQuerySelectedCampaignLore && canManageSelectedCampaignLore
        ? loreMigrationPreview === undefined
        : false,
      worlds,
      worldsLoading: includeWorldSummaries ? myWorlds === undefined : false,
      activeCampaignId,
    };
  }, [whoAmI, myPreferences, myCampaigns, myWorlds, standalone, roster, rosterRowsForDashboard, myAnnouncements, campaignAnnouncements, revealedLore, loreMigrationPreview, presence, selectedCampaignId, selectedCampaignIsKnown, selectedCampaignHasWorld, shouldQuerySelectedCampaignLore, canManageSelectedCampaignLore, includeWorldSummaries, globalFeatureFlags]);
};
